import type { QueryClient } from "@tanstack/react-query";

import { getListNurseryEventTypeConfigsQueryControllerListQueryKey } from "@/generated/event-type-configs/event-type-configs";
import { EventLifecycleRole } from "@/generated/model/eventLifecycleRole";
import type { NurseryEventTypeConfigResponseDto } from "@/generated/model/nurseryEventTypeConfigResponseDto";

/**
 * Pure logic behind the per-nursery event acknowledgement rules screen (NAU-342).
 *
 * Kept out of the component so it is unit-testable: this app's vitest config only
 * collects `.ts`, so anything living in a `.tsx` has no unit coverage at all.
 */

/** Draft state for one event type while the group admin is editing. */
export interface EventTypeRulesDraft {
  acknowledgerRoles: EventLifecycleRole[];
  resolverRoles: EventLifecycleRole[];
}

export type RulesDraft = Record<string, EventTypeRulesDraft>;

/**
 * Display order and labels. Derived from the generated enum rather than a local
 * union, so a role added server-side surfaces as a failing test instead of a blank
 * checkbox — the drift that a hand-written copy of a server enum has caused here
 * before.
 */
export const EVENT_LIFECYCLE_ROLE_ORDER: EventLifecycleRole[] = [
  EventLifecycleRole.MANAGER,
  EventLifecycleRole.DEPUTY_MANAGER,
  EventLifecycleRole.GROUP_ADMIN,
];

export const EVENT_LIFECYCLE_ROLE_LABELS: Record<EventLifecycleRole, string> = {
  [EventLifecycleRole.MANAGER]: "Manager",
  [EventLifecycleRole.DEPUTY_MANAGER]: "Deputy Manager",
  [EventLifecycleRole.GROUP_ADMIN]: "Group Admin",
};

export function eventLifecycleRoleLabel(role: EventLifecycleRole): string {
  return EVENT_LIFECYCLE_ROLE_LABELS[role] ?? role;
}

/** Seeds the editable draft from what the server reports as effective. */
export function draftFromConfigs(
  configs: NurseryEventTypeConfigResponseDto[],
): RulesDraft {
  const draft: RulesDraft = {};
  for (const config of configs) {
    draft[config.eventTypeId] = {
      acknowledgerRoles: [...config.acknowledgerRoles],
      resolverRoles: [...config.resolverRoles],
    };
  }
  return draft;
}

export function toggleRole(
  roles: EventLifecycleRole[],
  role: EventLifecycleRole,
): EventLifecycleRole[] {
  return roles.includes(role)
    ? roles.filter((existing) => existing !== role)
    : [...roles, role];
}

/** Order-insensitive, so merely re-ticking a box does not look like a change. */
function sameRoles(
  left: EventLifecycleRole[],
  right: EventLifecycleRole[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return [...left].sort().join(",") === [...right].sort().join(",");
}

export function isRowDirty(
  config: NurseryEventTypeConfigResponseDto,
  draft: EventTypeRulesDraft | undefined,
): boolean {
  if (!draft) {
    return false;
  }
  if (!sameRoles(config.acknowledgerRoles, draft.acknowledgerRoles)) {
    return true;
  }
  return !sameRoles(config.resolverRoles, draft.resolverRoles);
}

/** Only the rows that actually changed are written — one PUT per event type. */
export function changedEventTypeIds(
  configs: NurseryEventTypeConfigResponseDto[],
  draft: RulesDraft,
): string[] {
  return configs
    .filter((config) => isRowDirty(config, draft[config.eventTypeId]))
    .map((config) => config.eventTypeId);
}

export function isDraftDirty(
  configs: NurseryEventTypeConfigResponseDto[],
  draft: RulesDraft,
): boolean {
  return changedEventTypeIds(configs, draft).length > 0;
}

/**
 * Each action needs at least one role, or a record of that type could never leave
 * the Awaiting tab (no acknowledger) or never be closed (no resolver). Validated
 * before any write, which is why this screen has one Save button rather than
 * per-row autosave: unticking the last role would otherwise race the save.
 */
export function rowValidationError(
  draft: EventTypeRulesDraft | undefined,
): string | null {
  if (!draft) {
    return null;
  }
  if (draft.acknowledgerRoles.length === 0) {
    return "Choose at least one role who must acknowledge";
  }
  if (draft.resolverRoles.length === 0) {
    return "Choose at least one role who can mark resolved";
  }
  return null;
}

export function isDraftValid(draft: RulesDraft): boolean {
  return Object.values(draft).every((row) => rowValidationError(row) === null);
}

/**
 * Changing the rules changes what every Events Hub row allows, so the record
 * queries have to go too — the capability flags on cached rows were resolved under
 * the old configuration.
 */
export async function invalidateAcknowledgementRuleQueries(
  queryClient: QueryClient,
  nurseryId: string,
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey:
      getListNurseryEventTypeConfigsQueryControllerListQueryKey(nurseryId),
  });
}

/**
 * "a Manager", "a Manager or Group Admin" — the roles whose acknowledgement
 * actually closes a record of this type.
 *
 * Needed on the record detail page because the product has two things called
 * acknowledgement: the configured sign-off that moves the record on, and a
 * request asking a named person to read it. Naming the role turns "Awaiting
 * acknowledgement" from a state into an instruction, and stops a manager
 * assuming the outstanding request is what the record is waiting for.
 *
 * Ordered by seniority rather than by however the row was stored, so the same
 * configuration always reads the same way.
 */
export function describeAcknowledgerRoles(
  roles: EventLifecycleRole[] | undefined,
): string | null {
  if (!roles || roles.length === 0) {
    return null;
  }
  const labels = EVENT_LIFECYCLE_ROLE_ORDER.filter((role) =>
    roles.includes(role),
  ).map(eventLifecycleRoleLabel);
  if (labels.length === 0) {
    return null;
  }
  if (labels.length === 1) {
    return `a ${labels[0]}`;
  }
  const last = labels[labels.length - 1];
  return `a ${labels.slice(0, -1).join(", ")} or ${last}`;
}
